import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Plus, Pencil, Trash2, ToggleLeft, ToggleRight } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TransportManagement = () => {
  const [transports, setTransports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTransport, setEditingTransport] = useState(null);
  const [formData, setFormData] = useState({
    vehicle_name: '',
    vehicle_type: 'Bus',
    capacity: 0,
    price_per_trip: 0
  });

    const handleToggleActive = async (itemId, currentStatus) => {
    try {
      await axios.put(`${API}/master/transportations/${itemId}`, { is_active: !currentStatus });
      toast.success('Status berhasil diubah');
      fetchTransports();
    } catch (error) {
      toast.error('Gagal mengubah status');
    }
  };

const fetchTransports = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/master/transportations?show_all=true`);
      setTransports(response.data);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error fetching transports:', error);
      }
      toast.error('Gagal memuat data transportasi');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTransports();
  }, [fetchTransports]);

  const handleOpenModal = (transport = null) => {
    if (transport) {
      setEditingTransport(transport);
      setFormData({
        vehicle_name: transport.vehicle_name || '',
        vehicle_type: transport.vehicle_type || 'Bus',
        capacity: transport.capacity || 0,
        price_per_trip: transport.price_per_trip || 0
      });
    } else {
      setEditingTransport(null);
      setFormData({
        vehicle_name: '',
        vehicle_type: 'Bus',
        capacity: 0,
        price_per_trip: 0
      });
    }
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const payload = {
      ...formData,
      capacity: parseInt(formData.capacity),
      price_per_trip: parseFloat(formData.price_per_trip)
    };

    try {
      if (editingTransport) {
        await axios.put(`${API}/master/transportations/${editingTransport.id}`, payload);
        toast.success('Transportasi berhasil diupdate');
      } else {
        await axios.post(`${API}/master/transportations`, payload);
        toast.success('Transportasi berhasil ditambahkan');
      }
      setShowModal(false);
      fetchTransports();
    } catch (error) {
      toast.error(editingTransport ? 'Gagal update transportasi' : 'Gagal menambah transportasi');
    }
  };

  const handleDelete = async (transportId) => {
    if (!window.confirm('Yakin ingin menghapus transportasi ini?')) return;

    try {
      await axios.delete(`${API}/master/transportations/${transportId}`);
      toast.success('Transportasi berhasil dihapus');
      fetchTransports();
    } catch (error) {
      toast.error('Gagal menghapus transportasi');
    }
  };

  if (loading) {
    return <div className="text-center py-8">Memuat data...</div>;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold font-['Outfit']">Transport Management</h1>
        <Button className="rounded-full" onClick={() => handleOpenModal()}>
          <Plus className="h-4 w-4 mr-2" />
          Tambah Transport
        </Button>
      </div>

      <Card className="rounded-2xl">
        <CardHeader>
          <CardTitle>Daftar Transportasi</CardTitle>
        </CardHeader>
        <CardContent>
          {transports.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">
              Belum ada data transportasi
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left p-4 font-semibold">Nama Armada</th>
                    <th className="text-left p-4 font-semibold">Tipe</th>
                    <th className="text-left p-4 font-semibold">Kapasitas</th>
                    <th className="text-left p-4 font-semibold">Harga</th>
                    <th className="text-left p-4 font-semibold">Status</th>
                    <th className="text-right p-4 font-semibold">Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {transports.map((transport) => (
                    <tr key={transport.id} className="border-b hover:bg-muted/50">
                      <td className="p-4 font-medium">{transport.vehicle_name}</td>
                      <td className="p-4 text-muted-foreground">
                        <Badge>{transport.vehicle_type}</Badge>
                      </td>
                      <td className="p-4">{transport.capacity} orang</td>
                      <td className="p-4">
                        Rp {transport.price_per_trip?.toLocaleString('id-ID')}
                      </td>
                      <td className="p-4">
                        <Badge variant={transport.is_active ? 'default' : 'secondary'}>
                          {transport.is_active ? 'Aktif' : 'Nonaktif'}
                        </Badge>
                      </td>
                      <td className="p-4 text-right">
                        <div className="flex justify-end gap-2">
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleToggleActive(transport.id, transport.is_active)}
                          >
                            {transport.is_active ? <ToggleRight className="h-4 w-4" /> : <ToggleLeft className="h-4 w-4" />}
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleOpenModal(transport)}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="destructive" 
                            size="sm"
                            onClick={() => handleDelete(transport.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {editingTransport ? 'Edit Transport' : 'Tambah Transport Baru'}
            </DialogTitle>
            <DialogDescription>
              Lengkapi form di bawah untuk {editingTransport ? 'mengupdate' : 'menambahkan'} transportasi
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmit}>
            <div className="grid gap-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="vehicle_name">Nama Armada *</Label>
                <Input
                  id="vehicle_name"
                  value={formData.vehicle_name}
                  onChange={(e) => setFormData({...formData, vehicle_name: e.target.value})}
                  required
                  placeholder="Toyota Hiace"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="vehicle_type">Tipe Kendaraan *</Label>
                <select
                  id="vehicle_type"
                  className="w-full p-2 border border-input rounded-md"
                  value={formData.vehicle_type}
                  onChange={(e) => setFormData({...formData, vehicle_type: e.target.value})}
                  required
                >
                  <option value="Sedan">Sedan</option>
                  <option value="Hiace">Hiace</option>
                  <option value="Bus">Bus</option>
                  <option value="VIP">VIP</option>
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="capacity">Kapasitas *</Label>
                  <Input
                    id="capacity"
                    type="number"
                    value={formData.capacity}
                    onChange={(e) => setFormData({...formData, capacity: e.target.value})}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="price_per_trip">Harga per Hari *</Label>
                  <Input
                    id="price_per_trip"
                    type="number"
                    value={formData.price_per_trip}
                    onChange={(e) => setFormData({...formData, price_per_trip: e.target.value})}
                    required
                  />
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowModal(false)}>
                Batal
              </Button>
              <Button type="submit">
                {editingTransport ? 'Update' : 'Tambah'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TransportManagement;